import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../common/InfoTooltip";
import { formatDateOnlyYYYYMMDD } from "../../utils/dateOnly";
import {
  deleteAssignment,
  getStaffTrainingSummary,
  listTrainingModules,
  upsertUserAssignment,
} from "../../api/training";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "overdue", label: "Overdue" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

const TOOLTIP_TEXT = {
  profile:
    "Shows training assigned to this staff member and their progress.",
  assigned: "Total modules currently assigned.",
  completed: "Modules this staff member finished.",
  inProgress: "Modules started but not finished.",
  notStarted: "Modules not started yet.",
  overdue: "Modules past their due date.",
  search: "Find training by module name or category.",
  status: "Filter the list by training progress.",
  assignModule: "Add a training module to this staff member.",
  module: "Choose the training module to assign.",
  priority: "Marks how important this training is.",
  required: "Turn on when this training must be completed.",
  source:
    "Role = assigned to everyone with that role. User = assigned only to this staff member.",
  dueDate: "Training is overdue if due date is before today and not completed.",
  unassign: "Removes the user-specific assignment only.",
};

const EMPTY_VALUE = "--";

const formatCompletedAt = (value, timeZone) => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (err) {
    return date.toLocaleString();
  }
};

export default function StaffTrainingProfileCard({
  userId,
  token,
  cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6",
}) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "all" });
  const [assignDraft, setAssignDraft] = useState({
    module_id: "",
    due_on: "",
    priority: "normal",
    is_required: true,
  });

  const loadSummary = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await getStaffTrainingSummary(userId, token);
      setSummary(data || null);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
        setSummary(null);
        setError("");
      } else {
        setError(
          err?.response?.data?.detail || "Unable to load staff training summary."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  const loadModules = useCallback(async () => {
    if (!token) return;
    try {
      const list = await listTrainingModules(token);
      const published = Array.isArray(list)
        ? list.filter((module) => module.is_published)
        : [];
      setModules(published);
    } catch (err) {
      setModules([]);
    }
  }, [token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const trainingProEnabled = Boolean(summary?.training_pro_enabled);
  const items = Array.isArray(summary?.items) ? summary.items : [];

  const filteredItems = useMemo(() => {
    const query = (filters.search || "").trim().toLowerCase();
    return items.filter((item) => {
      if (filters.status === "overdue") {
        if (!item.is_overdue) return false;
      } else if (filters.status && filters.status !== "all") {
        if (item.status !== filters.status) return false;
      }
      if (!query) return true;
      const haystack = `${item.module_title || ""} ${item.category_name || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [items, filters]);

  const handleAssign = async () => {
    if (!assignDraft.module_id || !userId) return;
    setError("");
    setNotice("");
    try {
      await upsertUserAssignment(
        {
          userId,
          moduleId: Number(assignDraft.module_id),
          due_on: trainingProEnabled ? assignDraft.due_on || null : null,
          priority: trainingProEnabled ? assignDraft.priority : "normal",
          is_required: trainingProEnabled ? assignDraft.is_required : true,
        },
        token
      );
      setNotice("Assignment saved.");
      setAssignDraft({
        module_id: "",
        due_on: "",
        priority: "normal",
        is_required: true,
      });
      await loadSummary();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to assign module.");
    }
  };

  const handleUnassign = async (assignmentId) => {
    if (!assignmentId) return;
    const confirmed = window.confirm(
      "Remove this training assignment from this user? If it is also assigned by role, it will remain assigned."
    );
    if (!confirmed) return;
    setError("");
    setNotice("");
    try {
      await deleteAssignment(assignmentId, token);
      setNotice("Assignment removed.");
      await loadSummary();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to remove assignment.");
    }
  };

  if (!userId || forbidden) {
    return null;
  }

  return (
    <div id="section-training" className={`${cardClass} scroll-mt-24 space-y-4`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              Training Profile & Training Plan
            </h2>
            <InfoTooltip
              text={TOOLTIP_TEXT.profile}
              label="Training Profile"
              maxWidth={280}
            />
          </div>
          <p className="text-sm md:text-base text-slate-500">
            View assigned training, progress, and completion for this staff member.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-600">
            Assigned {summary?.stats?.assigned_total ?? 0}
            <InfoTooltip text={TOOLTIP_TEXT.assigned} label="Assigned" />
          </span>
          <span className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
            Completed {summary?.stats?.completed_total ?? 0}
            <InfoTooltip text={TOOLTIP_TEXT.completed} label="Completed" />
          </span>
          <span className="flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700">
            In progress {summary?.stats?.in_progress_total ?? 0}
            <InfoTooltip text={TOOLTIP_TEXT.inProgress} label="In progress" />
          </span>
          <span className="flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 font-semibold text-slate-600">
            Not started {summary?.stats?.not_started_total ?? 0}
            <InfoTooltip text={TOOLTIP_TEXT.notStarted} label="Not started" />
          </span>
          {trainingProEnabled && (
            <span className="flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 font-semibold text-rose-700">
              Overdue {summary?.stats?.overdue_total ?? 0}
              <InfoTooltip text={TOOLTIP_TEXT.overdue} label="Overdue" />
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading training profile...</div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : (
        <>
          {notice && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {notice}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_200px]">
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Search
                <InfoTooltip
                  text={TOOLTIP_TEXT.search}
                  label="Training search"
                  maxWidth={240}
                />
              </label>
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
                placeholder="Search by module or category"
                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Status
                <InfoTooltip
                  text={TOOLTIP_TEXT.status}
                  label="Training status"
                  maxWidth={240}
                />
              </label>
              <select
                value={filters.status}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, status: event.target.value }))
                }
                className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
              >
                {STATUS_OPTIONS.filter((option) =>
                  trainingProEnabled ? true : option.value !== "overdue"
                ).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Module</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Assigned On</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Progress</th>
                  {trainingProEnabled && (
                    <th className="px-3 py-2 text-left">
                      <span className="inline-flex items-center gap-2">
                        Due
                        <InfoTooltip text={TOOLTIP_TEXT.dueDate} label="Due date" />
                      </span>
                    </th>
                  )}
                  <th className="px-3 py-2 text-left">Completed</th>
                  <th className="px-3 py-2 text-left">
                    <span className="inline-flex items-center gap-2">
                      Source
                      <InfoTooltip text={TOOLTIP_TEXT.source} label="Source" />
                    </span>
                  </th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredItems.length ? (
                  filteredItems.map((item) => {
                    const sources = item.assignment_sources || [];
                    const hasRole = sources.some((src) => src.target_type === "role");
                    const hasUser = sources.some((src) => src.target_type === "user");
                    return (
                      <tr key={item.module_id}>
                        <td className="px-3 py-2 text-slate-800 font-semibold">
                          {item.module_title}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {item.category_name || EMPTY_VALUE}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {formatDateOnlyYYYYMMDD(item.assigned_on) || EMPTY_VALUE}
                        </td>
                        <td className="px-3 py-2 text-slate-600 capitalize">
                          {item.is_overdue ? "Overdue" : item.status.replace("_", " ")}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {Math.round(item.completion_pct || 0)}%
                        </td>
                        {trainingProEnabled && (
                          <td className="px-3 py-2 text-slate-600">
                            {item.due_on
                              ? formatDateOnlyYYYYMMDD(item.due_on)
                              : EMPTY_VALUE}
                          </td>
                        )}
                        <td className="px-3 py-2 text-slate-600">
                          {formatCompletedAt(item.completed_at, summary?.salon_timezone)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-2 text-[11px]">
                            {hasRole && (
                              <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                                Role
                              </span>
                            )}
                            {hasUser && (
                              <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">
                                User
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-3 text-xs">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/manager/hr/training?moduleId=${item.module_id}`)
                              }
                              className="font-semibold text-slate-700"
                            >
                              Open
                            </button>
                            {item.user_assignment_id && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleUnassign(item.user_assignment_id)}
                                  className="font-semibold text-rose-600"
                                >
                                  Unassign
                                </button>
                                <InfoTooltip
                                  text={TOOLTIP_TEXT.unassign}
                                  label="Unassign"
                                />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={trainingProEnabled ? 9 : 8}
                      className="px-3 py-4 text-sm text-slate-500"
                    >
                      No training assignments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              Assign a module to this user
              <InfoTooltip
                text={TOOLTIP_TEXT.assignModule}
                label="Assign module"
                maxWidth={260}
              />
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_200px_200px]">
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Module
                  <InfoTooltip text={TOOLTIP_TEXT.module} label="Module" />
                </label>
                <select
                  value={assignDraft.module_id}
                  onChange={(event) =>
                    setAssignDraft((prev) => ({ ...prev, module_id: event.target.value }))
                  }
                  className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                >
                  <option value="">Select a module</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.title}
                    </option>
                  ))}
                </select>
              </div>
              {trainingProEnabled ? (
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Due date
                    <InfoTooltip text={TOOLTIP_TEXT.dueDate} label="Due date" />
                  </label>
                  <input
                    type="date"
                    value={assignDraft.due_on}
                    onChange={(event) =>
                      setAssignDraft((prev) => ({ ...prev, due_on: event.target.value }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                  />
                </div>
              ) : (
                <div />
              )}
              {trainingProEnabled ? (
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Priority
                    <InfoTooltip text={TOOLTIP_TEXT.priority} label="Priority" />
                  </label>
                  <select
                    value={assignDraft.priority}
                    onChange={(event) =>
                      setAssignDraft((prev) => ({ ...prev, priority: event.target.value }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div />
              )}
            </div>
            {trainingProEnabled && (
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={Boolean(assignDraft.is_required)}
                  onChange={(event) =>
                    setAssignDraft((prev) => ({
                      ...prev,
                      is_required: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border border-slate-300 text-emerald-600"
                />
                Required
                <InfoTooltip text={TOOLTIP_TEXT.required} label="Required" />
              </label>
            )}
            <div className="mt-4">
              <button
                type="button"
                onClick={handleAssign}
                className="h-10 rounded-full bg-slate-900 px-4 text-sm font-semibold text-white"
              >
                Assign
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
